import api from '@/lib/axios';
import type { PaginatedResult } from '@/components/data-table';

function unwrap<T>(r: { data: { data?: T } | T }): T {
  const body: any = r.data;
  return (body?.data ?? body) as T;
}

export type IdCardTab = 'students' | 'staff';
export type IdCardStatusFilter = 'pending' | 'verified' | 'all';

export interface IdCardVerificationRow {
  id: string;
  name: string;
  photoUrl: string | null;
  admissionNo: string | null;
  employeeId: string | null;
  className: string | null;
  sectionName: string | null;
  designation: string | null;
  idCardVerified: boolean;
  idCardRequired: boolean;
}

export interface IdCardVerificationParams {
  tab: IdCardTab;
  page?: number;
  limit?: number;
  search?: string;
  classId?: string;
  sectionId?: string;
  status?: IdCardStatusFilter;
}

export interface BulkVerifyParams {
  tab: IdCardTab;
  ids?: string[];
  classId?: string;
  sectionId?: string;
}

export interface StudentIdCardEdit {
  id: string;
  photoUrl: string | null;
  firstName: string;
  lastName: string | null;
  dob: string | null;
  mobile: string | null;
  guardianFirstName: string | null;
  guardianLastName: string | null;
  guardianMobile: string | null;
  address: string | null;
}

export interface UpdateStudentIdCardParams {
  firstName: string;
  lastName?: string;
  dob?: string;
  mobile?: string;
  guardianFirstName?: string;
  guardianLastName?: string;
  guardianMobile?: string;
  address?: string;
  lock?: boolean;
}

export type RotateDirection = 'left' | 'right';

export const IdCardsApi = {
  verificationQueue: async (params: IdCardVerificationParams): Promise<PaginatedResult<IdCardVerificationRow> & { pendingCount: number }> =>
    unwrap(await api.get('/id-cards/verification', { params })),

  verify: async (id: string, tab: IdCardTab): Promise<{ id: string; idCardVerified: boolean }> =>
    unwrap(await api.post(`/id-cards/verification/${id}/verify`, { tab })),

  unverify: async (id: string, tab: IdCardTab): Promise<{ id: string; idCardVerified: boolean }> =>
    unwrap(await api.post(`/id-cards/verification/${id}/unverify`, { tab })),

  bulkVerify: async (params: BulkVerifyParams): Promise<{ verifiedCount: number }> =>
    unwrap(await api.post('/id-cards/verification/bulk-verify', params)),

  previewHtml: async (id: string, tab: IdCardTab): Promise<string> => {
    const res = await api.get(`/id-cards/verification/${id}/preview`, { params: { tab }, responseType: 'text' });
    return res.data as unknown as string;
  },

  getEditData: async (id: string): Promise<StudentIdCardEdit> => unwrap(await api.get(`/id-cards/verification/${id}/edit`)),

  update: async (id: string, params: UpdateStudentIdCardParams): Promise<{ id: string; idCardVerified: boolean }> =>
    unwrap(await api.patch(`/id-cards/verification/${id}`, params)),

  rotatePhoto: async (id: string, tab: IdCardTab, direction: RotateDirection): Promise<{ photoUrl: string }> =>
    unwrap(await api.post(`/id-cards/verification/${id}/photo/rotate`, null, { params: { tab, direction } })),
};
